/* @jsxImportSource @emotion/react */

import { css } from '@emotion/react'
import { AnimatePresence, motion } from 'motion/react'

import { SMALL_BREAKPOINT } from '../../../utils/breakpoints'
import type { AgentStep } from './steps'

const EASE = [0.4, 0, 0.2, 1] as const

const rowCss = css`
  position: relative;
  padding: 22px 0 22px 40px;
  border-bottom: 1px solid var(--alpha-shade-45);

  &:last-of-type {
    border-bottom: none;
  }

  @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
    padding: 16px 0 16px 28px;
  }
`

/* Description sits outside the button so its links aren't <a> in <button>. */
const titleButtonCss = css`
  display: block;
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  text-align: left;
  font: inherit;
  cursor: pointer;
`

const titleRowCss = css`
  display: flex;
  align-items: baseline;
  gap: 16px;
  font-size: 22px;
  line-height: 1.3;
  transition: color 0.25s ease;

  @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
    font-size: 18px;
    gap: 12px;
  }
`

const barTrackCss = css`
  position: absolute;
  left: 0;
  top: 18px;
  bottom: 18px;
  width: 3px;
  border-radius: 3px;
  overflow: hidden;
`

const barFillCss = css`
  display: block;
  width: 100%;
  height: 100%;
  border-radius: inherit;
  transform-origin: top;
`

const descCss = css`
  overflow: hidden;
`

const descTextCss = css`
  max-width: 460px;
`

type StepListProps = {
  steps: AgentStep[]
  activeStep: number
  /** Bumped on manual re-selection so the progress bar restarts. */
  cycleKey: number
  stepDurationMs: number
  onSelect: (index: number) => void
}

export function StepList({
  steps,
  activeStep,
  cycleKey,
  stepDurationMs,
  onSelect,
}: StepListProps) {
  return (
    <div>
      {steps.map((step, index) => {
        const isActive = index === activeStep
        return (
          <div key={step.id} css={rowCss}>
            {isActive && (
              <span
                css={barTrackCss}
                style={{
                  background: `color-mix(in srgb, ${step.color} 25%, transparent)`,
                }}
              >
                <motion.span
                  key={`${activeStep}-${cycleKey}`}
                  css={barFillCss}
                  style={{ background: step.color }}
                  initial={{ scaleY: 0 }}
                  animate={{ scaleY: 1 }}
                  transition={{
                    duration: stepDurationMs / 1000,
                    ease: 'linear',
                  }}
                />
              </span>
            )}
            <button
              type="button"
              css={titleButtonCss}
              aria-expanded={isActive}
              onClick={() => onSelect(index)}
            >
              <span
                css={titleRowCss}
                style={{ color: isActive ? step.color : 'var(--shade-00)' }}
              >
                <span className="font-weight-bold">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span>
                  <span className="font-weight-bold">{step.titleLead}</span>{' '}
                  <span
                    style={{
                      color: isActive ? step.color : 'var(--shade-30)',
                      fontWeight: 400,
                    }}
                  >
                    {step.titleRest}
                  </span>
                </span>
              </span>
            </button>
            <AnimatePresence initial={false}>
              {isActive && (
                <motion.div
                  key="desc"
                  css={descCss}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.4, ease: EASE }}
                >
                  <p
                    className="font-size-5 line-height-large shade-10 padding-top-12"
                    css={descTextCss}
                  >
                    {step.description}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )
      })}
    </div>
  )
}
